import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { recall } from "@/lib/forms";
import { Video, Clock, CalendarDays, ChevronLeft, ChevronRight, Mail } from "lucide-react";

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Overview({ name, desc, dur, accent, onPick }) {
  return (
    <div className="tf-choice rounded-lg p-5 space-y-3" data-testid="calendlo-overview">
      <div className="text-xl font-medium" data-testid="calendlo-event-title">{name}</div>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm opacity-80">
        <span className="inline-flex items-center gap-1.5"><Clock size={14} />{dur} min</span>
        <span className="inline-flex items-center gap-1.5"><Video size={14} />Google Meet</span>
      </div>
      {desc && <div className="text-sm opacity-70 whitespace-pre-line" data-testid="calendlo-event-description">{desc}</div>}
      <MeetNote />
      <button type="button" className="tf-choice rounded-md px-4 py-2.5 text-sm font-medium" style={{ background: accent, borderColor: accent, color: "#000" }} onClick={onPick} data-testid="calendlo-pick-time">Pick a time</button>
    </div>
  );
}

export const MeetNote = () => (
  <div className="text-xs opacity-70 inline-flex items-start gap-1.5" data-testid="calendlo-meet-note">
    <Mail size={13} className="mt-0.5 shrink-0" />This is a Google Meet video call. You'll get a calendar invite with the Meet link at your email.
  </div>
);

function Month({ month, setMonth, avail, day, setDay, accent }) {
  const y = month.getFullYear(), mo = month.getMonth();
  const lead = (new Date(y, mo, 1).getDay() + 6) % 7;
  const n = new Date(y, mo + 1, 0).getDate();
  const total = Math.max(42, Math.ceil((lead + n) / 7) * 7);
  const cells = Array.from({ length: total }, (_, i) => new Date(y, mo, i - lead + 1));
  return (
    <div data-testid="calendlo-month">
      <div className="flex items-center mb-2">
        <button type="button" className="p-1.5 opacity-70 hover:opacity-100" onClick={() => setMonth(new Date(y, mo - 1, 1))} data-testid="calendlo-prev-month"><ChevronLeft size={16} /></button>
        <div className="flex-1 text-center text-sm font-medium">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
        <button type="button" className="p-1.5 opacity-70 hover:opacity-100" onClick={() => setMonth(new Date(y, mo + 1, 1))} data-testid="calendlo-next-month"><ChevronRight size={16} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WD.map((w) => <div key={w} className="text-[10px] uppercase opacity-50 py-1">{w}</div>)}
        {cells.map((d) => {
          const k = ymd(d), ok = avail.has(k), on = k === day, other = d.getMonth() !== mo;
          return (
            <button key={k} type="button" disabled={!ok} onClick={() => setDay(k)} data-testid={`calendlo-day-${k}`}
              className={`relative aspect-square rounded-full text-sm transition-colors ${ok ? "font-medium" : `${other ? "opacity-10" : "opacity-25"} cursor-default`} ${other && ok ? "opacity-80" : ""}`}
              style={on ? { background: accent, color: "#000" } : ok ? { background: `${accent}${other ? "14" : "22"}`, color: accent } : undefined}>
              {d.getDate()}
              {other && d.getDate() === 1 && <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[8px] uppercase tracking-wider opacity-70">{d.toLocaleDateString("en-IN", { month: "short" })}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function CalendloTimes({ slug, blockId, value, onChange, accent, reloadKey, exclude, slotsUrl }) {
  const [data, setData] = useState(null);
  const [day, setDay] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  useEffect(() => {
    setData(null);
    api.get(slotsUrl || `/public/forms/${slug}/slots`, { params: slotsUrl ? { exclude } : { block_id: blockId, exclude }, timeout: 30000 }).then((r) => setData(r.data)).catch(() => setData({ slots: [], failed: true }));
  }, [slug, blockId, reloadKey, exclude, slotsUrl]);
  const byDay = useMemo(() => {
    const m = new Map();
    (data?.slots || []).forEach((s) => { if (!m.has(s.date)) m.set(s.date, []); m.get(s.date).push(s); });
    return m;
  }, [data]);
  useEffect(() => {
    if (!byDay.size || day) return;
    const first = [...byDay.keys()][0];
    setDay(first);
    setMonth(new Date(first + "T00:00:00"));
  }, [byDay, day]);
  if (!data) return <div className="text-sm opacity-60" data-testid="calendlo-loading">Finding open times…</div>;
  if (!byDay.size) return <div className="text-sm opacity-70" data-testid="calendlo-empty">{data.failed ? "Couldn't load times. Please refresh." : "No open times right now. We'll reach out to schedule."}</div>;
  return (
    <div className="grid sm:grid-cols-[1fr_200px] gap-5" data-testid="calendlo-picker">
      <Month month={month} setMonth={setMonth} avail={new Set(byDay.keys())} day={day} setDay={setDay} accent={accent} />
      <div>
        <div className="text-sm font-medium mb-2">{day ? new Date(day + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short" }) : ""}</div>
        <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-1">
          {(byDay.get(day) || []).map((s) => (
            <button key={s.start} type="button" onClick={() => onChange(s.start)} data-testid={`calendlo-slot-${s.start}`}
              className="tf-choice py-2.5 rounded-md text-sm transition-colors border" style={s.start === value ? { borderColor: accent, background: accent, color: "#000" } : { borderColor: `${accent}66`, color: accent }}>{s.time_label}</button>
          ))}
        </div>
        <div className="text-[11px] opacity-50 mt-3">Times in IST · {data.duration_min} min</div>
      </div>
    </div>
  );
}

export default function Calendlo({ b, slug, value, onChange, accent, reloadKey, preview, vars }) {
  const [step, setStep] = useState(b.display_mode === "calendar" || value ? "calendar" : "overview");
  useEffect(() => { setStep(b.display_mode === "calendar" ? "calendar" : "overview"); }, [b.display_mode]);
  const dur = b.meeting_duration_min || 30;
  const name = recall(b.event_name, vars) || "Discovery call";
  const desc = recall(b.event_description, vars);
  if (step === "overview") return <Overview name={name} desc={desc} dur={dur} accent={accent} onPick={() => setStep("calendar")} />;
  return (
    <div className="space-y-4" data-testid="calendlo">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm opacity-80">
        {b.display_mode !== "calendar" && <button type="button" className="underline opacity-70" onClick={() => setStep("overview")} data-testid="calendlo-back">← Back</button>}
        <span className="font-medium">{name}</span>
        <span className="inline-flex items-center gap-1"><Clock size={13} />{dur} min</span>
        <span className="inline-flex items-center gap-1"><Video size={13} />Google Meet</span>
      </div>
      {preview ? <div className="text-sm opacity-60 inline-flex items-center gap-2"><CalendarDays size={14} />Live calendar appears on the published form.</div>
        : <CalendloTimes slug={slug} blockId={b.id} value={value} onChange={onChange} accent={accent} reloadKey={reloadKey} />}
      <MeetNote />
    </div>
  );
}
